"use client";

import React, { FC, useContext, useMemo } from "react";

import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toLower, kebabCase } from "lodash";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { CombinedLink } from "~/components/combined-link/component";
import { JsonLd } from "~/components/json-ld/component";
import { MapView } from "~/components/map/component";
import { MapWrapper } from "~/components/map-wrapper/component";
import { Tag } from "~/components/tag/component";
import { TagType } from "~/components/tag/types";
import { Routes, Variables } from "~/constants/request";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { getLinkUrl } from "~/helpers/locales";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useClickAndCollectTrialStore } from "~/hooks/use-click-and-collect-trial-store";
import { Bullet24 } from "~/theme/icons/components/bullet";
import { Directions24 } from "~/theme/icons/components/directions";
import { Marker as MarkerIcon } from "~/theme/icons/components/marker";
import { Right24 } from "~/theme/icons/components/right";
import { getFormatMedia } from "~/theme/utils";
import { formatCityLine, formatStoreTitle } from "~/utils/format-store-details";
import { getJsonLdForBreadcrumbs } from "~/utils/jsonLd/breadcrumbs";

import { Hours } from "./components/hours/component";
import { MAIN_DETAILS_CLASSES } from "./constants";
import { MainDetailsContainer } from "./styled";
import { MainDetailsProps } from "./types";

export const MainDetails: FC<MainDetailsProps> = ({
  store,
  translations,
  handleGetDirectionsClick,
}) => {
  const { locale } = useParams<GlobalPathParams>();

  const dictionary = useContext(DictionaryLabelContext);
  const countryName = useVariable<string>(Variables.COUNTRY_NAME);

  const { shouldDisplayCCTag } = useClickAndCollectTrialStore(store);

  const theme = useTheme();
  const isTabletDownView = !useMediaQuery(
    getFormatMedia(theme).BREAKPOINT_WIDE_DESKTOP,
  );

  const coordinates = store
    ? {
        lat: +store?.displayCoordinate.latitude,
        lng: +store?.displayCoordinate.longitude,
      }
    : null;

  const url = `https://www.google.com/maps/search/?api=1&query=${store?.address?.line1},${store?.address?.city}&query_place_id=${store?.googlePlaceId}`;

  const facilities = store?.facilities
    ?.map((facility) => facility?.replace(/_/g, " "))
    .map(toLower);

  const phoneNumber = useMemo(
    () => store?.phoneNumberFormattedNational ?? store?.phoneNumber,
    [store?.phoneNumberFormattedNational, store?.phoneNumber],
  );

  const links = [
    { text: store?.helpTitle, url: store?.helpLink },
    { text: store?.faqTitle, url: store?.faqUrl },
  ];

  const handlePhoneClick = () => {
    if (isTabletDownView) {
      location.href = `tel:${store?.phoneNumber}`;
    }
  };

  const breadcrumbsData = useMemo(() => {
    const cityTitle = store?.address?.city;
    return [
      {
        title: countryName,
        urlSlug: getLinkUrl(locale, Routes.STORES_SEARCH),
      },
      {
        title: store?.address?.city,
        urlSlug: getLinkUrl(
          locale,
          `${Routes.STORES_SEARCH}/${kebabCase(cityTitle ?? "")}`,
        ),
      },
    ];
  }, [store, countryName, locale]);
  const breadcrumbJsonLd = useMemo(() => {
    return getJsonLdForBreadcrumbs(breadcrumbsData);
  }, [breadcrumbsData]);

  return (
    <MainDetailsContainer {...getTestAutomationProps("main-details")}>
      <Breadcrumbs className={MAIN_DETAILS_CLASSES.breadcrumbs}>
        {breadcrumbsData.map((item) => (
          <CombinedLink key={item.title} href={item.urlSlug}>
            {item.title}
          </CombinedLink>
        ))}
      </Breadcrumbs>
      <JsonLd data={breadcrumbJsonLd} />
      <Box className={MAIN_DETAILS_CLASSES.card} {...getTestAutomationProps("card")}>
        <Grid sx={{ marginTop: { xs: -1, sm: -1.25 } }} container>
          <Grid item md={6} className={MAIN_DETAILS_CLASSES.hideOnMobile}>
            <Box
              className={MAIN_DETAILS_CLASSES.map}
              {...getTestAutomationProps("map")}
            >
              <MapWrapper>
                <MapView
                  zoom={16}
                  center={coordinates}
                  gestureHandling="none"
                  disableDefaultUI={true}
                  clickableIcons={false}
                />
                <MarkerIcon />
              </MapWrapper>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              className={MAIN_DETAILS_CLASSES.info}
              {...getTestAutomationProps("info")}
            >
              <Typography
                variant="h1Regular"
                className={MAIN_DETAILS_CLASSES.infoTitle}
                {...getTestAutomationProps("title")}
              >
                {formatStoreTitle(store, locale)}
              </Typography>
              {shouldDisplayCCTag && (
                <Tag
                  className={MAIN_DETAILS_CLASSES.clickAndCollectTrialStoreTag}
                  type={TagType.clickAndCollectTrialStore}
                  title={dictionary?.[DictionaryKeys.ClickPlusCollectTrialStore]}
                />
              )}
              <Typography variant="body1" className={MAIN_DETAILS_CLASSES.infoLabel}>
                {translations?.addressText}
              </Typography>
              <Typography
                variant="body1"
                className={MAIN_DETAILS_CLASSES.infoText}
                component="div"
              >
                <Box {...getTestAutomationProps("address-street")}>
                  {[store?.address?.line1, store?.address?.line2]
                    .filter((i) => i)
                    .join(", ")}
                </Box>
                <Box {...getTestAutomationProps("address-city")}>
                  {formatCityLine(store?.address, locale)}
                </Box>
                <Box {...getTestAutomationProps("address-country")}>
                  {countryName}
                </Box>
              </Typography>
              <Button
                variant="outlined"
                className={MAIN_DETAILS_CLASSES.button}
                startIcon={<Directions24 />}
                href={url}
                onClick={(e) => handleGetDirectionsClick?.(e, url)}
                target="_BLANK"
                {...getTestAutomationProps("button")}
              >
                {translations?.getDirectionsText}
              </Button>
              <Typography variant="body1" className={MAIN_DETAILS_CLASSES.infoLabel}>
                {translations?.phoneText}
              </Typography>
              <Typography
                variant="body1"
                onClick={handlePhoneClick}
                className={MAIN_DETAILS_CLASSES.infoText}
                {...getTestAutomationProps("phone")}
              >
                {phoneNumber}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Grid container>
        <Grid item md={3} className={MAIN_DETAILS_CLASSES.hideOnMobile} />
        <Grid item xs={12} md={6}>
          <Hours
            hours={store?.hours ?? undefined}
            timeZone={store?.timezone ?? ""}
            special={store?.additionalHoursText ?? ""}
            translations={translations}
          />
          <Hours
            regularSchedule={store?.hours ?? undefined}
            hours={store?.holidayHours ?? undefined}
            timeZone={store?.timezone ?? ""}
            translations={translations}
          />
          {store?.parkingAddress && (
            <Box className={MAIN_DETAILS_CLASSES.details}>
              <Typography
                variant="body1"
                className={MAIN_DETAILS_CLASSES.detailsLabel}
              >
                {translations?.parkingAddressText}
              </Typography>
              <Typography
                variant="body1"
                className={MAIN_DETAILS_CLASSES.detailsText}
                {...getTestAutomationProps("parking-address")}
              >
                {store?.parkingAddress}
              </Typography>
            </Box>
          )}
          {facilities && (
            <Box className={MAIN_DETAILS_CLASSES.details}>
              <Typography
                variant="body1"
                className={MAIN_DETAILS_CLASSES.detailsLabel}
              >
                {translations?.facilitiesText}
              </Typography>
              <Typography
                variant="body1"
                className={MAIN_DETAILS_CLASSES.detailsText}
                component="div"
              >
                <ul>
                  {facilities?.map((facility, index) => (
                    <li
                      key={index}
                      {...getTestAutomationProps(`facility-${index + 1}`)}
                    >
                      <Bullet24 /> {` ${facility}`}
                    </li>
                  ))}
                </ul>
              </Typography>
            </Box>
          )}
          <Box className={MAIN_DETAILS_CLASSES.details}>
            {links?.map(({ text, url }, index) => (
              <CombinedLink
                key={index}
                href={url?.toString()}
                className={MAIN_DETAILS_CLASSES.link}
                {...getTestAutomationProps(`link-${index + 1}`)}
              >
                <Typography
                  variant="body1"
                  className={MAIN_DETAILS_CLASSES.linkText}
                >
                  {text}
                </Typography>
                <Right24 />
              </CombinedLink>
            ))}
          </Box>
        </Grid>
        <Grid item md={3} className={MAIN_DETAILS_CLASSES.hideOnMobile} />
      </Grid>
    </MainDetailsContainer>
  );
};
